import { useState, useEffect } from 'react';
import i18n from '../../i18n/i18n';
import { useNavigate } from 'react-router-dom';

// Components
import Logo from '../../components/Logo';
import SignEvalButton from '../../components/SignEvalButton';
import CardAccount from '../../components/auth/CardAccount';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CircularProgress } from '@mui/material';

// Helpers
import { useTheme } from '../../hooks/useTheme';
import { useUserStore } from '../../store/UserStore';
import { getUserInfo, getUser } from '../../helpers/api/callUser';
import { UserAccountInfo } from '../../types/userData';

const AccountPicker = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { setUser } = useUserStore();
    const [isLoading, setIsLoading] = useState(true);
    const [userAccounts, setUserAccounts] = useState<UserAccountInfo[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<UserAccountInfo>();

    useEffect(() => {
        const fetchUserInfo = async () => {
            const userInfo = await getUserInfo();
            userInfo && setUserAccounts(userInfo);
            setIsLoading(false);
        };

        fetchUserInfo();
    }, []);

    const handleSelectAccount = (account: UserAccountInfo) => {
        setSelectedAccount(account);
    };

    const handleContinue = async () => {
        if (!selectedAccount) return;
        setUser({
            id: selectedAccount.id,
            group: selectedAccount.group,
            profile: selectedAccount.profile
        });

        const userDetails = await getUser(selectedAccount.id);
        if (userDetails) {
            setUser(userDetails);
        } else {
            console.error('Failed to fetch user details');
            return;
        }

        navigate('/sessions');
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
                marginRight: 'auto',
                width: '100%',
                height: '100vh',
                backgroundColor: theme.palette.background.default
            }}
        >
            <Logo size="large" />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    width: '75%',
                    maxWidth: '25em',
                    maxHeight: '70%',
                    padding: 2,
                    gap: 2,
                    overflowY: 'auto'
                }}
            >
                {isLoading ? (
                    <CircularProgress sx={{ color: theme.palette.text.custom }} />
                ) : (
                    <>
                        <Typography
                            sx={{
                                textAlign: 'center',
                                marginBottom: '0.3em',
                                fontSize: '0.9em',
                                color: theme.palette.text.primary
                            }}
                        >
                            {i18n.t('AccountCard.selectAccount')}
                        </Typography>

                        {userAccounts.map((account) => (
                            <CardAccount key={account.id} account={account} onClick={() => handleSelectAccount(account)} selected={selectedAccount?.id === account.id} />
                        ))}

                        <SignEvalButton size="large" backgroundColor="blue" sx={{ mt: 2 }} onClick={handleContinue} disabled={!selectedAccount}>
                            {i18n.t('Login.link.logIn')}
                        </SignEvalButton>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default AccountPicker;
